import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { GroupTalkReportModal } from "../components/GroupTalkReportModal";
import { SafeMarkdown } from "../components/SafeMarkdown";
import { useCurrentCharacterAvatar } from "../hooks/useCurrentCharacterAvatar";
import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import { usePreferredCallName } from "../hooks/usePreferredCallName";
import { api, type GroupTalkReportResponse } from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  buildGroupTalkParticipantImageMap,
  findGroupTalkMember,
  getGroupTalkFallbackImageSrc,
  getGroupTalkDisplayMode,
  getGroupTalkImageSrc,
  type GroupTalkTemplateId,
} from "../lib/groupTalk";
import {
  clearGroupTalkActiveSessionSnapshot,
  loadGroupTalkActiveSessionSnapshot,
  saveGroupTalkActiveSessionSnapshot,
  type GroupTalkActiveSessionSnapshot,
} from "../lib/groupTalkSessionStorage";
import {
  DEFAULT_GROUP_TALK_CHOICE_INPUT_PROMPT,
  GROUP_TALK_CHOICE_INPUT_MAX_LENGTH,
  GROUP_TALK_MESSAGE_MAX_LENGTH,
  buildGroupTalkInputChoiceMessage,
  getGroupTalkChoicePayloadId,
  normalizeGroupTalkIntakeView,
  normalizeGroupTalkSessionDetailView,
  normalizeGroupTalkSessionView,
  normalizeGroupTalkTurnView,
  type GroupTalkNextChoiceView,
  type GroupTalkTranscriptMessage,
  type GroupTalkTurnView,
} from "../lib/groupTalkView";
import {
  GROUP_TALK_REPORT_GENERAL_ERROR_MESSAGE,
  canRegenerateGroupTalkReport,
  getGroupTalkReportActionErrorMessage,
  hasGroupTalkSummaryMessage,
  isGroupTalkReportStale,
  markGroupTalkReportFresh,
} from "../lib/groupTalkReport";

const EXPERT_MESSAGE_DELAY_MS = 2000;
const SUMMARY_MESSAGE_DELAY_MS = 1500;
const THINKING_MESSAGE_ROTATE_MS = 1800;
const DEFAULT_THINKING_MESSAGES = ["みんなで考えています...", "論点を整理しています..."];
const INTAKE_THINKING_MESSAGE = "整理しています...";
const INTAKE_THINKING_MESSAGE_ID_PREFIX = "pending_intake";

function buildThinkingMessages(members: Array<{ memberId: string }>) {
  const memberIds = new Set(members.map((member) => member.memberId));
  const messages = ["みんなで考えています..."];

  if (memberIds.has("cfo") || memberIds.has("tax_accountant") || memberIds.has("tax")) {
    messages.push("数字面を整理しています...");
  }

  if (
    memberIds.has("lawyer") ||
    memberIds.has("social_insurance_advisor") ||
    memberIds.has("law") ||
    memberIds.has("labor")
  ) {
    messages.push("リスクや制度面を確認しています...");
  }

  if (memberIds.has("cmo") || memberIds.has("customer")) {
    messages.push("市場やユーザー目線を整理しています...");
  }

  if (memberIds.has("cto") || memberIds.has("pm") || memberIds.has("coo") || memberIds.has("consultant")) {
    messages.push("進め方をすり合わせています...");
  }

  return messages.length > 0 ? messages : DEFAULT_THINKING_MESSAGES;
}

function createThinkingMessage(text = DEFAULT_THINKING_MESSAGES[0], createdAt = new Date().toISOString()): GroupTalkTranscriptMessage {
  return {
    messageId: `pending_meeting_${Date.now()}`,
    participantId: null,
    memberId: null,
    speakerId: "meeting",
    speakerName: "会議メンバー",
    speakerType: "facilitator",
    roleLabel: "ファシリテータ",
    stanceId: null,
    stanceLabel: null,
    facilitator: true,
    phase: "thinking",
    sequence: null,
    content: text,
    nextChoices: [],
    createdAt,
  };
}

function createIntakeThinkingMessage(createdAt = new Date().toISOString()): GroupTalkTranscriptMessage {
  return {
    ...createThinkingMessage(INTAKE_THINKING_MESSAGE, createdAt),
    messageId: `${INTAKE_THINKING_MESSAGE_ID_PREFIX}_${Date.now()}`,
    speakerName: "日向 メイ",
  };
}

function isIntakeThinkingMessage(message: GroupTalkTranscriptMessage) {
  return message.messageId.startsWith(`${INTAKE_THINKING_MESSAGE_ID_PREFIX}_`);
}

function createPendingTurn(userMessage: string, speakerName: string): GroupTalkTurnView {
  const createdAt = new Date().toISOString();
  const userRow: GroupTalkTranscriptMessage = {
    messageId: `pending_user_${Date.now()}`,
    participantId: null,
    memberId: null,
    speakerId: "user",
    speakerName,
    speakerType: "user",
    roleLabel: "ユーザー",
    stanceId: null,
    stanceLabel: null,
    facilitator: false,
    phase: "user",
    sequence: 0,
    content: userMessage,
    nextChoices: [],
    createdAt,
  };

  return {
    sessionId: "",
    turnId: `pending_turn_${Date.now()}`,
    messages: [userRow, createIntakeThinkingMessage(createdAt)],
    createdAt,
    isSatisfied: false,
  };
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function splitMessagesForReveal(messages: GroupTalkTranscriptMessage[]) {
  const userMessages: GroupTalkTranscriptMessage[] = [];
  const intakeMessages: GroupTalkTranscriptMessage[] = [];
  const expertMessages: GroupTalkTranscriptMessage[] = [];
  const summaryMessages: GroupTalkTranscriptMessage[] = [];

  messages.forEach((message) => {
    if (message.phase === "user") {
      userMessages.push(message);
      return;
    }
    if (message.phase === "intake") {
      intakeMessages.push(message);
      return;
    }
    if (message.phase === "summary") {
      summaryMessages.push(message);
      return;
    }
    if (message.phase === "facilitator_direct") {
      summaryMessages.push(message);
      return;
    }
    if (message.phase === "expert") {
      expertMessages.push(message);
    }
  });

  return {
    initialMessages: [...userMessages, ...intakeMessages],
    expertMessages,
    summaryMessages,
  };
}

function mergeTurnMessages(intakeMessages: GroupTalkTranscriptMessage[], completionMessages: GroupTalkTranscriptMessage[]) {
  const completionBody = completionMessages
    .filter((message) => message.phase !== "user" && message.phase !== "intake")
    .sort((left, right) => {
      if (left.sequence === null && right.sequence === null) return 0;
      if (left.sequence === null) return 1;
      if (right.sequence === null) return -1;
      return left.sequence - right.sequence;
    });

  return [...intakeMessages, ...completionBody];
}

function findLatestChoiceMessageId(turns: GroupTalkTurnView[]) {
  const latestTurn = turns[turns.length - 1];
  if (!latestTurn) return null;

  for (let index = latestTurn.messages.length - 1; index >= 0; index -= 1) {
    const message = latestTurn.messages[index];
    if (message.phase === "thinking") continue;
    return (message.nextChoices ?? []).length > 0 ? message.messageId : null;
  }

  return null;
}

type SelectedChoiceState = {
  messageId: string;
  value: string;
  label: string;
};

type ChoiceInputState = {
  messageId: string;
  sourcePhase: GroupTalkTranscriptMessage["phase"];
  choice: GroupTalkNextChoiceView;
  value: string;
  error: string;
};

type SelectedChoiceContext = {
  messageId: string;
  sourcePhase: GroupTalkTranscriptMessage["phase"];
  choice: GroupTalkNextChoiceView;
};

function getChoiceInputPrompt(choice: GroupTalkNextChoiceView) {
  return choice.inputPrompt || DEFAULT_GROUP_TALK_CHOICE_INPUT_PROMPT;
}

function buildSelectedChoicePayload(context?: SelectedChoiceContext) {
  if (!context) return undefined;

  return {
    choiceId: getGroupTalkChoicePayloadId(context.choice),
    choiceType: context.choice.choiceType,
    actionIntent: context.choice.actionIntent,
    sourcePhase: context.choice.sourcePhase || context.sourcePhase,
    sourceMessageId: context.choice.sourceMessageId || context.messageId,
  };
}

export default function GroupTalkSession() {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const characterName = useCurrentCharacterName();
  const characterAvatarSrc = useCurrentCharacterAvatar();
  const preferredCallName = usePreferredCallName();
  const endRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);
  const initialComposerScrollDoneRef = useRef(false);
  const initialUserMessageSendKeyRef = useRef<string | null>(null);

  const [snapshot, setSnapshot] = useState<GroupTalkActiveSessionSnapshot | null>(() =>
    sessionId ? loadGroupTalkActiveSessionSnapshot(sessionId) : null
  );
  const [loadingSession, setLoadingSession] = useState(() =>
    sessionId ? !loadGroupTalkActiveSessionSnapshot(sessionId) : false
  );
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [pendingTurn, setPendingTurn] = useState<GroupTalkTurnView | null>(null);
  const [thinkingMessageIndex, setThinkingMessageIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<SelectedChoiceState | null>(null);
  const [activeChoiceInput, setActiveChoiceInput] = useState<ChoiceInputState | null>(null);
  const [report, setReport] = useState<GroupTalkReportResponse | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportCreating, setReportCreating] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const thinkingMessages = useMemo(
    () => (snapshot ? buildThinkingMessages(snapshot.session.members) : DEFAULT_THINKING_MESSAGES),
    [snapshot]
  );
  const displayMode = useMemo(() => getGroupTalkDisplayMode(snapshot?.draft.mode ?? snapshot?.draft.templateId), [snapshot]);

  const loadReport = useCallback(async (options?: { silent?: boolean }) => {
    if (!sessionId) return;

    const silent = options?.silent === true;
    if (!silent) {
      setReportLoading(true);
      setReportError("");
    }

    try {
      const response = await api.getGroupTalkSessionReport(sessionId);
      setReport(response);
      setReportError("");
    } catch (errorValue) {
      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }

      logError(errorValue);
      if (!silent) {
        setReportError(GROUP_TALK_REPORT_GENERAL_ERROR_MESSAGE);
      }
    } finally {
      if (!silent) {
        setReportLoading(false);
      }
    }
  }, [navigate, sessionId]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/talk/group", {
        replace: true,
        state: { errorMessage: "会議セッションが見つかりませんでした。設定画面からやり直してください。" },
      });
      return;
    }

    const loaded = loadGroupTalkActiveSessionSnapshot(sessionId);
    if (loaded) {
      setSnapshot(loaded);
      setLoadingSession(false);
      return;
    }

    let active = true;
    setLoadingSession(true);
    setLoadError("");

    (async () => {
      try {
        const response = await api.getGroupTalkSession(sessionId);
        if (!active) return;

        const normalized = normalizeGroupTalkSessionDetailView(
          response,
          characterName,
          getGroupTalkDisplayMode(response.template_id === "custom" ? "custom" : "named")
        );
        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          sessionId: normalized.session.sessionId,
          draft: {
            templateId: ((normalized.session.templateId as GroupTalkTemplateId | null) ?? "custom"),
            title: normalized.session.title,
            description: normalized.session.description,
            participants: normalized.session.members.map((member) => ({
              participantId: member.participantId,
              memberId: member.memberId,
              stanceId: member.stanceId,
              fixed: Boolean(findGroupTalkMember(member.memberId).fixed),
            })),
          },
          session: normalized.session,
          turns: normalized.turns,
        };

        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        setSnapshot(nextSnapshot);
      } catch (errorValue) {
        if (!active) return;

        if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }

        logError(errorValue);
        navigate("/talk/group", {
          replace: true,
          state: { errorMessage: "セッションを復元できませんでした。続きから始める一覧で再度お試しください。" },
        });
      } finally {
        if (active) {
          setLoadingSession(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [characterName, navigate, sessionId]);

  useEffect(() => {
    initialComposerScrollDoneRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [pendingTurn]);

  useEffect(() => {
    if (!snapshot || snapshot.turns.length === 0 || initialComposerScrollDoneRef.current) {
      return;
    }

    initialComposerScrollDoneRef.current = true;
    window.setTimeout(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 0);
  }, [snapshot]);

  useEffect(() => {
    const hasThinkingMessage = pendingTurn?.messages.some(
      (message) => message.phase === "thinking" && !isIntakeThinkingMessage(message)
    );
    if (!hasThinkingMessage) {
      setThinkingMessageIndex(0);
      return;
    }

    const timer = window.setInterval(() => {
      setThinkingMessageIndex((current) => (current + 1) % thinkingMessages.length);
    }, THINKING_MESSAGE_ROTATE_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [pendingTurn, thinkingMessages]);

  useEffect(() => {
    setPendingTurn((current) => {
      if (!current) return current;
      const hasThinkingMessage = current.messages.some((message) => message.phase === "thinking");
      if (!hasThinkingMessage) return current;

      return {
        ...current,
        messages: current.messages.map((message) =>
          message.phase === "thinking" && !isIntakeThinkingMessage(message)
            ? { ...message, content: thinkingMessages[thinkingMessageIndex] }
            : message
        ),
      };
    });
  }, [thinkingMessageIndex, thinkingMessages]);

  const allTurns = useMemo(() => {
    if (!snapshot) return pendingTurn ? [pendingTurn] : [];
    return pendingTurn ? [...snapshot.turns, pendingTurn] : snapshot.turns;
  }, [pendingTurn, snapshot]);

  const latestChoiceMessageId = useMemo(() => findLatestChoiceMessageId(allTurns), [allTurns]);

  const participantImageMap = useMemo(
    () => (snapshot ? buildGroupTalkParticipantImageMap(snapshot.session.members, displayMode) : new Map<string, string>()),
    [displayMode, snapshot]
  );

  const sendUserMessage = useCallback(async (
    messageText: string,
    choice?: SelectedChoiceContext
  ): Promise<boolean> => {
    const trimmed = messageText.trim();
    if (!snapshot || !trimmed || sending) return false;

    if (choice) {
      setSelectedChoice({
        messageId: choice.messageId,
        value: choice.choice.value,
        label: choice.choice.label,
      });
    }

    setSending(true);
    setLoadError("");
    setComposerText("");
    const createdPendingTurn = createPendingTurn(trimmed, preferredCallName);
    setPendingTurn(createdPendingTurn);

    try {
      const intakeResponse = await api.createGroupTalkIntake(snapshot.session.sessionId, {
        userMessage: trimmed,
        selectedChoice: buildSelectedChoicePayload(choice),
      });

      const nextSession = normalizeGroupTalkSessionView(
        {
          ...snapshot.session,
          session_id: intakeResponse.session_id,
          template_id: snapshot.session.templateId,
          title: snapshot.session.title,
          description: snapshot.session.description,
          members: snapshot.session.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            display_name: member.label,
            role: member.role,
            stance: member.stanceId,
          })),
          agreed_notes: intakeResponse.agreed_notes,
          session_state: intakeResponse.session_state,
          status: snapshot.session.status,
          created_at: snapshot.session.createdAt,
        },
        characterName,
        displayMode
      );

      const intakeTurn = normalizeGroupTalkIntakeView(intakeResponse, nextSession, characterName, displayMode);
      const fallbackUserMessages = createdPendingTurn.messages.filter((message) => message.phase === "user");
      const intakeMessages = intakeTurn.messages.length > 0 ? intakeTurn.messages : fallbackUserMessages;

      setPendingTurn({
        sessionId: intakeTurn.sessionId,
        turnId: intakeTurn.turnId,
        messages:
          intakeTurn.needsFollowUp || !intakeTurn.continueToken
            ? intakeMessages
            : [...intakeMessages, createThinkingMessage(thinkingMessages[Math.min(1, thinkingMessages.length - 1)])],
        createdAt: intakeTurn.createdAt,
        isSatisfied: false,
      });

      if (intakeTurn.needsFollowUp || !intakeTurn.continueToken) {
        const nextTurn: GroupTalkTurnView = {
          sessionId: intakeTurn.sessionId,
          turnId: intakeTurn.turnId,
          messages: intakeMessages,
          createdAt: intakeTurn.createdAt,
          isSatisfied: false,
        };

        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          ...snapshot,
          session: {
            ...nextSession,
            agreedNotes: intakeTurn.agreedNotes,
            sessionState: intakeTurn.sessionState,
          },
          turns: [...snapshot.turns, nextTurn],
        };

        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        setSnapshot(nextSnapshot);
        setPendingTurn(null);
        return true;
      }

      const completionResponse = await api.createGroupTalkTurnComplete(snapshot.session.sessionId, intakeTurn.turnId, {
        continueToken: intakeTurn.continueToken,
      });

      const completedSession = normalizeGroupTalkSessionView(
        {
          ...nextSession,
          session_id: completionResponse.session_id,
          template_id: nextSession.templateId,
          title: nextSession.title,
          description: nextSession.description,
          members: nextSession.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            display_name: member.label,
            role: member.role,
            stance: member.stanceId,
          })),
          agreed_notes: completionResponse.agreed_notes,
          session_state: completionResponse.session_state,
          status: nextSession.status,
          created_at: nextSession.createdAt,
        },
        characterName,
        displayMode
      );

      const completedTurn = normalizeGroupTalkTurnView(completionResponse, completedSession, characterName, displayMode);
      const mergedMessages = mergeTurnMessages(intakeMessages, completedTurn.messages);
      const { initialMessages, expertMessages, summaryMessages } = splitMessagesForReveal(mergedMessages);
      const revealedInitialMessages = initialMessages.length > 0 ? initialMessages : fallbackUserMessages;
      const reportShouldRefreshAfterTurn =
        report?.status === "completed" || report?.status === "failed" || report?.status === "generating";
      const completedReportShouldBecomeStale =
        report?.status === "completed" && (expertMessages.length > 0 || summaryMessages.length > 0);

      setPendingTurn({
        ...completedTurn,
        messages: revealedInitialMessages,
      });

      for (const message of expertMessages) {
        await wait(EXPERT_MESSAGE_DELAY_MS);
        setPendingTurn((current) => {
          if (!current || current.turnId !== completedTurn.turnId) return current;
          return {
            ...current,
            messages: [...current.messages, message],
          };
        });
      }

      for (const message of summaryMessages) {
        await wait(SUMMARY_MESSAGE_DELAY_MS);
        setPendingTurn((current) => {
          if (!current || current.turnId !== completedTurn.turnId) return current;
          return {
            ...current,
            messages: [...current.messages, message],
          };
        });
      }

      const nextSnapshot: GroupTalkActiveSessionSnapshot = {
        ...snapshot,
        session: completedSession,
        turns: [
          ...snapshot.turns,
          {
            ...completedTurn,
            messages: mergedMessages,
          },
        ],
      };

      saveGroupTalkActiveSessionSnapshot(nextSnapshot);
      setSnapshot(nextSnapshot);
      setPendingTurn(null);
      if (completedReportShouldBecomeStale) {
        setReport((current) =>
          current?.status === "completed"
            ? {
                ...current,
                is_stale: true,
                can_regenerate: true,
              }
            : current
        );
      }
      if (reportShouldRefreshAfterTurn) {
        void loadReport({ silent: true });
      }
      return true;
    } catch (errorValue) {
      logError(errorValue);
      setPendingTurn(null);
      if (!choice) {
        setComposerText(trimmed);
      }
      if (choice) {
        setSelectedChoice((current) => (current?.messageId === choice.messageId ? null : current));
      }

      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        clearGroupTalkActiveSessionSnapshot(snapshot.session.sessionId);
        navigate("/splash", { replace: true });
        return false;
      }

      setLoadError(toUserMessage(errorValue));
      return false;
    } finally {
      setSending(false);
    }
  }, [characterName, displayMode, loadReport, navigate, preferredCallName, report, sending, snapshot, thinkingMessages]);

  useEffect(() => {
    if (!snapshot || sending || pendingTurn) return;
    const initialUserMessage = snapshot.initialUserMessage?.trim();
    if (!initialUserMessage || snapshot.initialUserMessageSent) return;

    const sendKey = `${snapshot.sessionId}:${initialUserMessage}`;
    if (initialUserMessageSendKeyRef.current === sendKey) return;
    initialUserMessageSendKeyRef.current = sendKey;

    void (async () => {
      const succeeded = await sendUserMessage(initialUserMessage);
      setSnapshot((current) => {
        if (!current || current.sessionId !== snapshot.sessionId) return current;
        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          ...current,
          initialUserMessage: undefined,
          initialUserMessageSent: succeeded,
        };
        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        return nextSnapshot;
      });
      if (!succeeded) {
        initialUserMessageSendKeyRef.current = null;
      }
    })();
  }, [pendingTurn, sending, snapshot, sendUserMessage]);

  const handleSend = async () => {
    await sendUserMessage(composerText);
  };

  const handleGenerateReport = async () => {
    if (!sessionId || reportCreating || report?.status === "generating") return;

    setReportCreating(true);
    setReportError("");

    try {
      const response = await api.generateGroupTalkSessionReport(sessionId);
      setReport(markGroupTalkReportFresh(response));
    } catch (errorValue) {
      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }

      if (errorValue instanceof AppError && errorValue.meta.status === 409) {
        setReport({ status: "generating" });
      }
      if (!(errorValue instanceof AppError) || (errorValue.meta.status !== 409 && errorValue.meta.status !== 422)) {
        logError(errorValue);
      }
      setReportError(getGroupTalkReportActionErrorMessage(errorValue));
    } finally {
      setReportCreating(false);
    }
  };

  const handleChoiceSelect = async (message: GroupTalkTranscriptMessage, choice: GroupTalkNextChoiceView) => {
    if (
      sending ||
      selectedChoice?.messageId === message.messageId ||
      activeChoiceInput?.messageId === message.messageId ||
      message.messageId !== latestChoiceMessageId ||
      choice.choiceType === "finish"
    ) {
      return;
    }

    if (choice.requiresInput) {
      setLoadError("");
      setActiveChoiceInput({
        messageId: message.messageId,
        sourcePhase: message.phase,
        choice,
        value: "",
        error: "",
      });
      return;
    }

    await sendUserMessage(choice.label, { messageId: message.messageId, sourcePhase: message.phase, choice });
  };

  const handleChoiceInputChange = (messageId: string, value: string) => {
    setActiveChoiceInput((current) =>
      current?.messageId === messageId
        ? {
            ...current,
            value,
            error:
              value.length > GROUP_TALK_CHOICE_INPUT_MAX_LENGTH
                ? `入力が長すぎます。${GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}文字以内で入力してください。`
                : "",
          }
        : current
    );
  };

  const handleChoiceInputCancel = (messageId: string) => {
    setActiveChoiceInput((current) => (current?.messageId === messageId ? null : current));
  };

  const handleChoiceInputSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeChoiceInput || sending || activeChoiceInput.messageId !== latestChoiceMessageId) return;

    const trimmedValue = activeChoiceInput.value.trim();
    if (!trimmedValue) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId ? { ...current, error: "追加情報を入力してください。" } : current
      );
      return;
    }

    if (trimmedValue.length > GROUP_TALK_CHOICE_INPUT_MAX_LENGTH) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId
          ? {
              ...current,
              error: `入力が長すぎます。${GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}文字以内で入力してください。`,
            }
          : current
      );
      return;
    }

    const userMessage = buildGroupTalkInputChoiceMessage(activeChoiceInput.choice, trimmedValue);
    if (userMessage.length > GROUP_TALK_MESSAGE_MAX_LENGTH) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId
          ? { ...current, error: "送信文が長すぎます。入力内容を短くしてください。" }
          : current
      );
      return;
    }

    const succeeded = await sendUserMessage(userMessage, {
      messageId: activeChoiceInput.messageId,
      sourcePhase: activeChoiceInput.sourcePhase,
      choice: activeChoiceInput.choice,
    });
    if (succeeded) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId ? null : current
      );
    }
  };

  if (!snapshot) {
    if (loadingSession) {
      return (
        <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
          <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-4xl items-center justify-center">
            <div className="rounded-[2rem] border border-[#EADBC8] bg-white px-6 py-5 text-sm font-bold text-[#6B5851] shadow-[0_20px_48px_rgba(124,90,78,0.10)]">
              会議を読み込んでいます...
            </div>
          </div>
        </main>
      );
    }

    return null;
  }

  const hasSummaryMessage = hasGroupTalkSummaryMessage(allTurns);
  const staleReport = isGroupTalkReportStale(report);
  const canRegenerateReport = canRegenerateGroupTalkReport(report);
  const shouldOfferReportUpdate = staleReport && canRegenerateReport;
  const canCreateReport = hasSummaryMessage || report?.status === "failed" || shouldOfferReportUpdate;
  const showReportButton =
    hasSummaryMessage || report?.status === "completed" || report?.status === "failed" || report?.status === "generating";
  const reportButtonLabel = reportLoading
    ? "確認中..."
    : reportCreating || report?.status === "generating"
      ? "作成中"
      : report?.status === "completed"
        ? shouldOfferReportUpdate
          ? "論点整理を更新"
          : "論点整理を見る"
        : report?.status === "failed"
          ? "もう一度作成する"
          : "論点整理を作成";

  return (
    <main className="h-screen overflow-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex h-full w-full max-w-4xl flex-col">
        <section className="sticky top-4 z-10 mb-4 rounded-[1.4rem] border border-[#EADBC8] bg-white/88 px-4 py-3 shadow-[0_12px_28px_rgba(124,90,78,0.10)] backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3">
            <Link to="/talk/group" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
              ←戻る
            </Link>
            <h1 className="truncate text-base font-black tracking-tight text-[#7C5A4E]">
              {snapshot.session.title}
            </h1>
            <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
              WizMate
            </Link>
          </div>
        </section>

        <section className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[2rem] border border-[#EADBC8] bg-white px-4 py-4 shadow-[0_20px_48px_rgba(124,90,78,0.10)]">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            {allTurns.flatMap((turn) =>
              turn.messages.map((message) => {
                const showParticipantImage = message.speakerType !== "user" && message.phase !== "thinking";
                const messageImageSrc = message.facilitator
                  ? characterAvatarSrc
                  : message.participantId
                    ? participantImageMap.get(message.participantId)
                    : message.memberId === "mei"
                      ? getGroupTalkImageSrc("mei")
                      : undefined;
                const nextChoices = message.nextChoices ?? [];
                const selectedChoiceValue =
                  selectedChoice?.messageId === message.messageId ? selectedChoice.value : null;
                const activeChoiceInputForMessage =
                  activeChoiceInput?.messageId === message.messageId ? activeChoiceInput : null;
                const showChoiceInput =
                  activeChoiceInputForMessage !== null &&
                  message.messageId === latestChoiceMessageId &&
                  selectedChoice?.messageId !== message.messageId;
                const choiceGroupDisabled =
                  message.messageId !== latestChoiceMessageId ||
                  sending ||
                  selectedChoice?.messageId === message.messageId ||
                  activeChoiceInputForMessage !== null;
                const isDirectArtifact = message.phase === "facilitator_direct";
                const messageFrameClass = isDirectArtifact
                  ? "flex w-full max-w-[84%] items-start gap-3 lg:max-w-[92%] xl:max-w-[1120px] 2xl:max-w-[1200px]"
                  : "flex max-w-[84%] items-start gap-3 lg:max-w-[68%]";

                return (
                  <React.Fragment key={message.messageId}>
                    <div className={message.speakerType === "user" ? "flex justify-end" : "flex justify-start"}>
                      <div className={messageFrameClass} data-testid={`group-talk-message-frame-${message.phase}`}>
                        {showParticipantImage ? (
                          <img
                            src={messageImageSrc ?? getGroupTalkFallbackImageSrc()}
                            alt={message.speakerName}
                            className="mt-1 h-10 w-10 shrink-0 rounded-full border border-[#EADBC8] bg-[#FFF7F1] object-cover"
                            onError={(event) => {
                              const target = event.currentTarget;
                              const fallbackSrc = getGroupTalkFallbackImageSrc();
                              if (target.getAttribute("src") === fallbackSrc) return;
                              target.src = fallbackSrc;
                            }}
                          />
                        ) : null}
                        <div
                          className={`min-w-0 rounded-[1.6rem] px-4 py-3 ${isDirectArtifact ? "w-full" : ""} ${
                            message.speakerType === "user"
                              ? "bg-[#5D4D46] text-white"
                              : message.facilitator
                                ? "border border-[#E8CDBE] bg-[#FFF1E8] text-[#4A3E39]"
                                : "border border-[#EADBC8] bg-[#FFFBF8] text-[#4A3E39]"
                          }`}
                          data-testid={`group-talk-message-bubble-${message.phase}`}
                        >
                          <p className={`text-xs font-black ${message.speakerType === "user" ? "text-[#F8EDE7]" : "text-[#A67C52]"}`}>
                            {message.speakerType === "user"
                              ? preferredCallName
                              : message.facilitator
                                ? characterName
                                : message.speakerName}
                            {message.speakerType !== "user" ? ` / ${message.roleLabel}` : ""}
                          </p>
                          {message.phase === "facilitator_direct" ? (
                            <div className="mt-2">
                              <SafeMarkdown text={message.content} tableVariant="artifact" />
                            </div>
                          ) : (
                            <p className="mt-2 whitespace-pre-wrap text-sm leading-7">{message.content}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {nextChoices.length > 0 ? (
                      <div className="ml-12 flex justify-start">
                        <div
                          className={`w-full max-w-[84%] rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-3 py-3 shadow-sm lg:max-w-[68%] ${
                            choiceGroupDisabled ? "opacity-75" : ""
                          }`}
                        >
                          <p className="px-1 text-xs font-bold text-[#9C867D]">次に進める方向を選んでください</p>
                          <div className="mt-2 flex flex-col gap-2">
                            {nextChoices.map((choice, choiceIndex) => {
                              const isSelected = selectedChoiceValue === choice.value;
                              const isFinishChoice = choice.choiceType === "finish";
                              const disabled = choiceGroupDisabled || isFinishChoice;
                              return (
                                <button
                                  key={`${message.messageId}-${choice.value}-${choiceIndex}`}
                                  type="button"
                                  disabled={disabled}
                                  onClick={() => void handleChoiceSelect(message, choice)}
                                  className={`min-h-[44px] rounded-[1rem] border px-3 py-2 text-left text-sm font-bold leading-6 transition ${
                                    isSelected
                                      ? "border-[#A67C52] bg-[#F8EBDD] text-[#5D4D46]"
                                      : "border-[#EADBC8] bg-white text-[#5D4D46] hover:border-[#CFAE9B] hover:bg-[#FFF7F1]"
                                  } disabled:cursor-not-allowed disabled:hover:border-[#EADBC8] disabled:hover:bg-white`}
                                >
                                  <span className="flex flex-wrap items-center gap-2">
                                    {isSelected ? (
                                      <span className="rounded-full bg-[#A67C52] px-2 py-0.5 text-[11px] font-black text-white">
                                        選択済み
                                      </span>
                                    ) : isFinishChoice ? (
                                      <span className="rounded-full bg-[#ECE4DD] px-2 py-0.5 text-[11px] font-black text-[#8B7A72]">
                                        終了
                                      </span>
                                    ) : choice.isRecommended ? (
                                      <span className="rounded-full bg-[#F4DED0] px-2 py-0.5 text-[11px] font-black text-[#8A5D4F]">
                                        おすすめ
                                      </span>
                                    ) : null}
                                    <span>{choice.label}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {showChoiceInput ? (
                            <form
                              className="mt-3 rounded-[1rem] border border-[#EADBC8] bg-[#FFF8F2] px-3 py-3"
                              onSubmit={(event) => void handleChoiceInputSubmit(event)}
                            >
                              <label
                                htmlFor={`choice-input-${message.messageId}`}
                                className="block text-xs font-bold text-[#7C5A4E]"
                              >
                                {getChoiceInputPrompt(activeChoiceInputForMessage.choice)}
                              </label>
                              <textarea
                                id={`choice-input-${message.messageId}`}
                                value={activeChoiceInputForMessage.value}
                                onChange={(event) => handleChoiceInputChange(message.messageId, event.currentTarget.value)}
                                placeholder={activeChoiceInputForMessage.choice.inputPlaceholder || undefined}
                                maxLength={GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}
                                rows={3}
                                disabled={sending}
                                className="mt-2 min-h-[88px] w-full resize-none rounded-[0.9rem] border border-[#EADBC8] bg-white px-3 py-2 text-sm leading-6 text-[#4A3E39] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20 disabled:cursor-not-allowed disabled:opacity-70"
                              />
                              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-xs font-bold text-[#9C867D]">
                                  {activeChoiceInputForMessage.value.length}/{GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}
                                </span>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleChoiceInputCancel(message.messageId)}
                                    disabled={sending}
                                    className="min-h-[40px] rounded-[0.9rem] border border-[#EADBC8] bg-white px-3 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#CFAE9B] hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    キャンセル
                                  </button>
                                  <button
                                    type="submit"
                                    disabled={sending || !activeChoiceInputForMessage.value.trim()}
                                    className="min-h-[40px] rounded-[0.9rem] bg-[#5D4D46] px-4 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {sending ? "送信中..." : "送信"}
                                  </button>
                                </div>
                              </div>
                              {activeChoiceInputForMessage.error ? (
                                <p role="alert" className="mt-2 text-xs font-bold text-[#C06D5A]">
                                  {activeChoiceInputForMessage.error}
                                </p>
                              ) : null}
                            </form>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          {loadError ? (
            <p role="alert" className="mt-4 text-sm font-bold text-[#C06D5A]">
              {loadError}
            </p>
          ) : null}

          <div ref={composerRef} className="mt-4 flex flex-col gap-3 border-t border-[#F0E7DF] pt-4 sm:flex-row sm:items-end">
            {showReportButton ? (
              <button
                type="button"
                onClick={() => setReportModalOpen(true)}
                disabled={reportLoading || reportCreating}
                className="min-h-[44px] shrink-0 rounded-[1.2rem] border border-[#A67C52] bg-white px-4 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {reportButtonLabel}
              </button>
            ) : null}
            <div className="flex min-w-0 flex-1 items-end gap-3">
              <textarea
                value={composerText}
                onChange={(event) => setComposerText(event.target.value)}
                placeholder="相談したいことを入力..."
                rows={3}
                disabled={sending}
                className="min-h-[84px] flex-1 resize-none rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
              />
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={sending || !composerText.trim()}
                className="shrink-0 rounded-[1.4rem] bg-[#5D4D46] px-5 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? "送信中..." : "送信"}
              </button>
            </div>
          </div>
        </section>
        <GroupTalkReportModal
          isOpen={reportModalOpen}
          report={report}
          loading={reportLoading}
          creating={reportCreating}
          errorMessage={reportError}
          canCreate={canCreateReport}
          onCreate={() => void handleGenerateReport()}
          onClose={() => setReportModalOpen(false)}
        />
      </div>
    </main>
  );
}
